import React, { useEffect, useMemo, useRef } from "react";
import PubNub from "pubnub";
import { env } from "@config";
import { Signal } from "@types";
import { decrypt } from "@utils";
import { useAppDispatch, useAppSelector } from "@store";
import { AddinTab, switchAddinTab } from "@store/global";
import { updateUserFlags } from "@store/auth";
import { DateRangeDefaults, getSignals, pushSignal } from "@store/signals";
import { useFirebase } from "./context";
import { AboutModal, BottomNav } from "./components";
import { AnalysisTabIcon, SignalsTabIcon, SimulatorTabIcon } from "./icons";
import {
  AnalysisScreen,
  DifferentUserScreen,
  MaintenanceScreen,
  SignalsScreen,
  SignInScreen,
  SimulatorScreen,
} from "./screens";
import css from "./App.module.scss";

const App: React.FC = () => {
  const dispatch = useAppDispatch();

  const user = useAppSelector((state) => state.auth.user);
  const isMaintenanceMode = useAppSelector((state) => state.global.isMaintenanceMode);
  const activeTabId = useAppSelector((state) => state.global.activeTabId);
  const signalFilters = useAppSelector((state) => state.signals.filters);
  const notViewedSignalsCount = useAppSelector(
    (state) => state.signals.pagination.notViewedSignalsCount
  );

  const { isLoggedIn, isInitialized } = useFirebase();

  const pubNubInstance = useRef<PubNub | null>(null);

  const tabs = useMemo(
    () => [
      {
        id: AddinTab.Signals,
        label: "Signals",
        icon: SignalsTabIcon,
        showIndicator: !!notViewedSignalsCount,
      },
      {
        id: AddinTab.Analysis,
        label: "Analysis",
        icon: AnalysisTabIcon,
      },
      {
        id: AddinTab.Simulator,
        label: "Simulator",
        icon: SimulatorTabIcon,
      },
    ],
    [notViewedSignalsCount]
  );

  const outlookProfileEmail =
    Office.context.mailbox?.userProfile?.emailAddress?.toLowerCase() || "";
  const isDifferentUser = !!outlookProfileEmail && !!user && outlookProfileEmail !== user.email;

  const attrs = {
    app: {
      className: css.app,
    },
    bottomNav: {
      tabs,
      activeTabId,
      disabled: !isLoggedIn || isDifferentUser || isMaintenanceMode,
      onTabClick: (tabId: AddinTab) => {
        dispatch(switchAddinTab(tabId));
      },
    },
  };

  const renderScreen = () => {
    if (!isLoggedIn && isInitialized) {
      return <SignInScreen />;
    }

    if (isMaintenanceMode) {
      return <MaintenanceScreen />;
    }

    if (isDifferentUser) {
      return <DifferentUserScreen />;
    }

    if (activeTabId === "signals") {
      return <SignalsScreen />;
    }

    if (activeTabId === "analysis") {
      return <AnalysisScreen />;
    }

    if (activeTabId === "simulator") {
      return <SimulatorScreen />;
    }

    return null;
  };

  useEffect(() => {
    if (user && !user.isAddinInstalled) {
      dispatch(
        updateUserFlags([
          {
            name: "isAddinInstalled",
            value: true,
          },
        ])
      );
    }
  }, [dispatch, user]);

  useEffect(() => {
    let request: Promise<unknown> & {
      abort: () => void;
    };

    if (
      user &&
      !isDifferentUser &&
      !isMaintenanceMode &&
      signalFilters.dateRangeString !== DateRangeDefaults.Custom
    ) {
      request = dispatch(getSignals());
    }

    return () => {
      request?.abort();
    };
  }, [dispatch, isDifferentUser, isMaintenanceMode, signalFilters, user]);

  useEffect(() => {
    if (user && !isDifferentUser && !isMaintenanceMode) {
      const currentPubNubInstance = new PubNub({
        subscribeKey: env.pubNubSubscribeKey,
        uuid: `${user.orgId}|${user.userId}`,
      });

      pubNubInstance.current = currentPubNubInstance;
    }
  }, [user, isDifferentUser, isMaintenanceMode]);

  useEffect(() => {
    const currentPubNubInstance = pubNubInstance.current;
    const listener = {
      message: ({ message }) => {
        const signal = decrypt<Signal>(message);

        // TODO: Check with BE the solution for SignalType.ThreadUpdated
        // if (signal.eventName === SignalType.ThreadUpdated && signal.eventMetadata.thread.replied) {
        //   dispatch(
        //     pushOpenedNotRepliedSignalToRemove(signal.eventMetadata.thread.platformThreadId)
        //   );
        //   return;
        // }

        // if (signal.eventName === SignalType.ThreadUpdated && signal.eventMetadata.thread.opened) {
        //   dispatch(pushNotOpenedSignalIdToRemove(signal.eventMetadata.thread.platformThreadId));
        //   return;
        // }

        dispatch(pushSignal(signal));
      },
      status: (status: PubNub.StatusEvent) => {
        if (env.mode !== "prod") {
          console.log("Pubnub status: ", status);
        }
      },
    };

    if (currentPubNubInstance && user && !isDifferentUser && !isMaintenanceMode) {
      currentPubNubInstance.addListener(listener);
      currentPubNubInstance.subscribe({
        channels: [`user-channel-${user.orgId}-${user.userId}`, `org-channel-${user.orgId}`],
      });
    }

    return () => {
      currentPubNubInstance?.removeListener(listener);
      currentPubNubInstance?.unsubscribeAll();
      pubNubInstance.current = null;
    };
  }, [dispatch, user, isDifferentUser, isMaintenanceMode]);

  return (
    <div {...attrs.app}>
      {renderScreen()}
      <AboutModal />
      <BottomNav {...attrs.bottomNav} />
    </div>
  );
};

export default App;
